import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ModeToggle } from '@/components/mode-toggle';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import { Loader2, Save, Play, Pause, Pencil, Check, Clock, Code2, Bell, X, FlaskConical, Share2, Blocks, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { isMobileViewport } from '../hooks/useMobile';
import { hasRealIntegration } from '../lib/integrationStatus';
import {
  apiClient,
  type WorkflowDefinition,
  type WorkflowNode,
  type WorkflowCatalog,
  type WorkflowIntegrationStatus,
  type WorkflowSummary,
} from '../api/client';
import { WorkflowCanvas } from '../components/workflows/WorkflowCanvas';
import { WorkflowSidebar } from '../components/workflows/WorkflowSidebar';
import { WorkflowNLInput } from '../components/workflows/WorkflowNLInput';
import { WorkflowSuggestionsChat } from '../components/WorkflowSuggestionsChat';
import { toast } from 'sonner';
import { NodeConfigPanel } from '../components/workflows/NodeConfigPanel';
import { CreateCustomBlockDialog } from '../components/workflows/CreateCustomBlockDialog';
import { SimulatedModePromptModal } from '../components/workflows/SimulatedModePromptModal';
import { WorkflowCodePanel } from '../components/workflows/WorkflowCodePanel';
import { DemoSignupModal } from '../components/workflows/DemoSignupModal';
import { DemoShareModal } from '../components/workflows/DemoShareModal';
import { IS_DEMO_MODE } from '../lib/demoMode';
import { getErrorMessage } from '../lib/errors';

const EMPTY_DEFINITION: WorkflowDefinition = { nodes: [], edges: [] };
const DEFAULT_TRIGGER_CONFIG = { source: 'kubernetes', signals: [], cooldown_hours: 24, max_concurrent_executions: 3 };

const WorkflowBuilder: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();

  const [workflow, setWorkflow] = useState<WorkflowSummary | null>(null);
  // Simulated builder mode: all integrations are presented as connected and
  // the workflow saves flagged as simulated (test-only, never activatable).
  // Seeded from ?simulated=1 (onboarding wizard) or the loaded workflow.
  // The public demo builder is always simulated.
  const [simulated, setSimulated] = useState(IS_DEMO_MODE || searchParams.get('simulated') === '1');
  // Prompt shown when a customer with zero connected integrations opens the
  // builder, offering to switch on the simulated environment.
  const [showSimulatedPrompt, setShowSimulatedPrompt] = useState(false);
  const [name, setName] = useState('Untitled Workflow');
  const [description, setDescription] = useState('Describe what this workflow does...');
  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [definition, setDefinition] = useState<WorkflowDefinition>(EMPTY_DEFINITION);
  const [triggerConfig, setTriggerConfig] = useState<any>({ ...DEFAULT_TRIGGER_CONFIG });
  const [alertConfig, setAlertConfig] = useState<{ enabled: boolean; channels: string[]; dm_user_ids: string[] }>({ enabled: false, channels: [], dm_user_ids: [] });
  const [costPerRun, setCostPerRun] = useState<number | null>(null);
  const [catalog, setCatalog] = useState<WorkflowCatalog | null>(null);
  const [integrations, setIntegrations] = useState<WorkflowIntegrationStatus[]>([]);
  const [githubRepos, setGithubRepos] = useState<string[]>([]);
  const [gitlabRepos, setGitlabRepos] = useState<string[]>([]);
  const [confluenceSpaces, setConfluenceSpaces] = useState<{ key: string; name: string }[]>([]);
  const [jiraProjects, setJiraProjects] = useState<{ key: string; name: string }[]>([]);
  const [linearTeams, setLinearTeams] = useState<{ key: string; name: string }[]>([]);
  const [vercelProjects, setVercelProjects] = useState<{ key: string; name: string }[]>([]);
  const [railwayProjects, setRailwayProjects] = useState<{ key: string; name: string }[]>([]);
  const [railwayServices, setRailwayServices] = useState<{ key: string; name: string }[]>([]);
  const [railwayEnvironments, setRailwayEnvironments] = useState<{ key: string; name: string }[]>([]);
  const [flyioApps, setFlyioApps] = useState<{ key: string; name: string }[]>([]);
  const [nebiusProjects, setNebiusProjects] = useState<{ key: string; name: string }[]>([]);
  const [nebiusClusters, setNebiusClusters] = useState<{ key: string; name: string }[]>([]);
  const [beamDeployments, setBeamDeployments] = useState<{ key: string; name: string }[]>([]);
  const [beamContainers, setBeamContainers] = useState<{ key: string; name: string }[]>([]);
  const [beamMachines, setBeamMachines] = useState<{ key: string; name: string }[]>([]);
  const [beamSandboxes, setBeamSandboxes] = useState<{ key: string; name: string }[]>([]);
  const [daytonaSandboxes, setDaytonaSandboxes] = useState<{ key: string; name: string }[]>([]);
  const [daytonaSnapshots, setDaytonaSnapshots] = useState<{ key: string; name: string }[]>([]);
  const [daytonaVolumes, setDaytonaVolumes] = useState<{ key: string; name: string }[]>([]);
  const [daytonaTargets, setDaytonaTargets] = useState<{ key: string; name: string }[]>([]);
  const [supabaseProjects, setSupabaseProjects] = useState<{ key: string; name: string }[]>([]);
  const [supabaseBranches, setSupabaseBranches] = useState<{ key: string; name: string }[]>([]);
  const [planetscaleDatabases, setPlanetscaleDatabases] = useState<{ key: string; name: string }[]>([]);
  const [planetscaleBranches, setPlanetscaleBranches] = useState<{ key: string; name: string }[]>([]);
  const [planetscaleDeployRequests, setPlanetscaleDeployRequests] = useState<{ key: string; name: string }[]>([]);
  const [neonProjects, setNeonProjects] = useState<{ key: string; name: string }[]>([]);
  const [neonBranches, setNeonBranches] = useState<{ key: string; name: string }[]>([]);
  const [neonEndpoints, setNeonEndpoints] = useState<{ key: string; name: string }[]>([]);
  const [clickhouseServices, setClickHouseServices] = useState<{ key: string; name: string }[]>([]);
  const [clickhouseClickPipes, setClickHouseClickPipes] = useState<{ key: string; name: string }[]>([]);
  const [clickhouseBackups, setClickHouseBackups] = useState<{ key: string; name: string }[]>([]);
  const [jenkinsJobs, setJenkinsJobs] = useState<{ key: string; name: string }[]>([]);
  const [circleciProjects, setCircleCIProjects] = useState<{ key: string; name: string }[]>([]);
  const [sonarcloudProjects, setSonarCloudProjects] = useState<{ key: string; name: string }[]>([]);
  const [oktaUsers, setOktaUsers] = useState<{ key: string; name: string }[]>([]);
  const [oktaGroups, setOktaGroups] = useState<{ key: string; name: string }[]>([]);
  const [terraformWorkspaces, setTerraformWorkspaces] = useState<{ key: string; name: string }[]>([]);
  const [databricksJobs, setDatabricksJobs] = useState<{ key: string; name: string }[]>([]);
  const [databricksClusters, setDatabricksClusters] = useState<{ key: string; name: string }[]>([]);
  const [databricksPipelines, setDatabricksPipelines] = useState<{ key: string; name: string }[]>([]);
  const [databricksWarehouses, setDatabricksWarehouses] = useState<{ key: string; name: string }[]>([]);
  const [pulumiStacks, setPulumiStacks] = useState<{ key: string; name: string }[]>([]);
  const [vaultMounts, setVaultMounts] = useState<{ key: string; name: string }[]>([]);
  const [vaultPolicies, setVaultPolicies] = useState<{ key: string; name: string }[]>([]);
  const [vaultDatabaseMounts, setVaultDatabaseMounts] = useState<{ key: string; name: string }[]>([]);
  const [infisicalProjects, setInfisicalProjects] = useState<{ key: string; name: string }[]>([]);
  const [infisicalSecretSyncs, setInfisicalSecretSyncs] = useState<{ key: string; name: string }[]>([]);
  const [clusters, setClusters] = useState<{ id: string; name: string }[]>([]);
  const [awsAccounts, setAwsAccounts] = useState<{ key: string; name: string }[]>([]);
  const [gcpProjects, setGcpProjects] = useState<{ key: string; name: string }[]>([]);
  const [nlPrompt, setNLPrompt] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fromRequestIdRef = React.useRef<string | null>(null);
  const fromSuggestionIdRef = React.useRef<string | null>(null);

  const [configPanelWidth, setConfigPanelWidth] = useState(320);
  const isResizingPanel = useRef(false);
  const resizePanelLineRef = useRef<HTMLDivElement>(null);
  const [showCreateCustomBlock, setShowCreateCustomBlock] = useState(false);
  const [editingCustomBlock, setEditingCustomBlock] = useState<import('../api/client').CustomBlock | null>(null);
  // Public demo builder: Save and Test are signup-gated — nothing is
  // persisted or executed for anonymous visitors.
  const [demoSignupCTA, setDemoSignupCTA] = useState<'save' | 'test' | null>(null);
  // Shareable snapshots (demo.usekestrel.ai/w/{id}).
  const [demoSharing, setDemoSharing] = useState(false);
  const [demoShareUrl, setDemoShareUrl] = useState('');
  // Bundle of shared demo workflows (demo.usekestrel.ai/b/{id}): the viewer
  // gets a picker to switch between the bundled snapshots.
  const [bundleWorkflows, setBundleWorkflows] = useState<Array<{ id: string; name: string; description: string; definition: any; trigger_config: any; nl_prompt: string }>>([]);
  const [bundleIndex, setBundleIndex] = useState(0);
  const [demoBundleUrl, setDemoBundleUrl] = useState('');
  const [demoBundleCreating, setDemoBundleCreating] = useState(false);
  // Share IDs published this browser session — lets the sharer collapse them
  // into one bundle link from the share modal.
  const [sessionShareIds, setSessionShareIds] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem('demo_share_session_ids') || '[]');
    } catch {
      return [];
    }
  });
  // Bumped when a whole new workflow lands on the canvas (NL generation) so
  // the canvas remounts and re-fits the view around the new graph.
  const [canvasResetKey, setCanvasResetKey] = useState(0);
  // Mobile-only: the block sidebar collapses into a slide-over drawer.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [codePanelWidth, setCodePanelWidth] = useState(400);
  const [codePanelRegenerateTrigger, setCodePanelRegenerateTrigger] = useState(0);
  const isResizingCodePanel = useRef(false);
  const resizeCodePanelLineRef = useRef<HTMLDivElement>(null);

  // In simulated mode every integration is presented as connected so all
  // sidebar blocks are draggable without real credentials.
  const effectiveIntegrations = useMemo(
    () => (simulated ? integrations.map(i => ({ ...i, connected: true })) : integrations),
    [integrations, simulated],
  );

  const enrichedDefinition = useMemo(() => {
    const customBlocks = catalog?.custom_blocks || [];
    if (customBlocks.length === 0) return definition;
    const blockMap = new Map(customBlocks.map(b => [`custom-${b.id}`, b]));
    const enrichedNodes = definition.nodes.map(n => {
      // Enrich custom action blocks
      if (n.data?.integration === 'custom') {
        const blockId = n.data.action;
        const cb = blockMap.get(blockId);
        if (!cb) return n;
        const groupIcon = customBlocks.find(b => b.integration_name === cb.integration_name && b.icon_url)?.icon_url || '';
        const groupIconDark = customBlocks.find(b => b.integration_name === cb.integration_name && b.icon_url_dark)?.icon_url_dark || '';
        return {
          ...n,
          data: {
            ...n.data,
            custom_icon_url: n.data.custom_icon_url || cb.icon_url || groupIcon || '',
            custom_icon_url_dark: n.data.custom_icon_url_dark || cb.icon_url_dark || groupIconDark || '',
            custom_color: n.data.custom_color || cb.color || '',
            integration_name: n.data.integration_name || cb.integration_name || '',
            description: n.data.description || cb.description || '',
            method: cb.method || '',
            custom_parameters: cb.parameters || [],
          },
        };
      }
      // Enrich custom webhook trigger blocks
      if (n.data?.source === 'custom_webhook') {
        let cb: typeof customBlocks[0] | undefined;
        // Try matching by custom_block_id first
        if (n.data.custom_block_id) {
          cb = customBlocks.find(b => b.id === n.data.custom_block_id);
        }
        // Fallback: match by template_id pattern (custom-trigger-{blockId})
        if (!cb && n.data.template_id) {
          const tid = (n.data.template_id as string).replace('custom-trigger-', '');
          cb = customBlocks.find(b => b.id === tid);
        }
        // Fallback: match by signal_type against any trigger block
        if (!cb) {
          cb = customBlocks.find(b => b.block_type === 'trigger');
        }
        if (cb) {
          const matchedBlock = cb;
          const groupIcon = customBlocks.find(b => b.integration_name === matchedBlock.integration_name && b.icon_url)?.icon_url || '';
          const groupIconDark = customBlocks.find(b => b.integration_name === matchedBlock.integration_name && b.icon_url_dark)?.icon_url_dark || '';
          return {
            ...n,
            data: {
              ...n.data,
              custom_icon_url: n.data.custom_icon_url || matchedBlock.icon_url || groupIcon || '',
              custom_icon_url_dark: n.data.custom_icon_url_dark || matchedBlock.icon_url_dark || groupIconDark || '',
              custom_color: n.data.custom_color || matchedBlock.color || '',
              integration_name: n.data.integration_name || matchedBlock.integration_name || '',
            },
          };
        }
      }
      return n;
    });
    return { ...definition, nodes: enrichedNodes };
  }, [definition, catalog]);

  // Compute per-execution cost from current definition
  const computedCostPerRun = useMemo(() => {
    const HEAVY_ACTIONS = new Set([
      'kestrel-trigger-rca', 'kestrel-trigger-cloud-rca', 'kestrel-find-causal-prs',
      'kestrel-investigate-cloud', 'kestrel-investigate-k8s',
      'github-investigate-code', 'github-generate-code-fix', 'vercel-investigate', 'trivy-investigate',
    ]);
    const REGULAR_ACTIONS = new Set([
      'kestrel-analyze-costs', 'kestrel-generate-k8s-manifest', 'kestrel-generate-helm-values',
      'kestrel-generate-cloud-resource', 'kestrel-create-gitops-pr', 'kestrel-create-iac-pr',
      'karpenter-scale-nodepool', 'karpenter-set-disruption', 'karpenter-apply-nodepool', 'karpenter-delete-nodeclaim',
      'kyverno-set-enforcement', 'kyverno-apply-policy', 'kyverno-delete-policy',
      'trivy-rescan-workload',
    ]);
    let total = 0;
    for (const node of definition.nodes) {
      const action = node.data?.action || node.type;
      if (HEAVY_ACTIONS.has(action)) total += 0.40;
      else if (REGULAR_ACTIONS.has(action)) total += 0.20;
      else total += 0.10;
    }
    return total;
  }, [definition.nodes]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isResizingPanel.current) {
        const baseRight = showCodePanel ? codePanelWidth + 9 : 0;
        const newWidth = Math.max(280, Math.min(600, window.innerWidth - e.clientX - baseRight));
        setConfigPanelWidth(newWidth);
      } else if (isResizingCodePanel.current) {
        const newWidth = Math.max(320, Math.min(800, window.innerWidth - e.clientX));
        setCodePanelWidth(newWidth);
      }
    };
    const onMouseUp = () => {
      if (isResizingPanel.current) {
        isResizingPanel.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (resizePanelLineRef.current) {
          resizePanelLineRef.current.style.transitionDelay = '0ms';
          resizePanelLineRef.current.style.backgroundColor = 'transparent';
        }
      }
      if (isResizingCodePanel.current) {
        isResizingCodePanel.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (resizeCodePanelLineRef.current) {
          resizeCodePanelLineRef.current.style.transitionDelay = '0ms';
          resizeCodePanelLineRef.current.style.backgroundColor = 'transparent';
        }
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [showCodePanel, codePanelWidth]);

  const startPanelResize = useCallback(() => {
    isResizingPanel.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (resizePanelLineRef.current) {
      resizePanelLineRef.current.style.transitionDelay = '0ms';
      resizePanelLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.5)';
    }
  }, []);

  const startCodePanelResize = useCallback(() => {
    isResizingCodePanel.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (resizeCodePanelLineRef.current) {
      resizeCodePanelLineRef.current.style.transitionDelay = '0ms';
      resizeCodePanelLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.5)';
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, id]);

  // Hydrate the builder from a shared demo snapshot (single share or a
  // bundle member).
  const applySharedSnapshot = (shared: { name?: string; description?: string; definition?: any; trigger_config?: any; nl_prompt?: string }) => {
    if (shared.name) setName(shared.name);
    if (shared.description) setDescription(shared.description);
    const sharedDef = shared.definition || EMPTY_DEFINITION;
    const sharedTC = shared.trigger_config || DEFAULT_TRIGGER_CONFIG;
    if (sharedTC?.signals?.[0]?.filters) {
      const triggerNode = sharedDef.nodes.find((n: any) => n.type === 'trigger');
      if (triggerNode) {
        triggerNode.data = { ...triggerNode.data, signal_filter: sharedTC.signals[0].filters };
      }
    }
    setDefinition(sharedDef);
    setTriggerConfig(sharedTC);
    // Set unconditionally: when switching between bundle members, a workflow
    // without a stored prompt must not inherit the previous tab's prompt.
    setNLPrompt(shared.nl_prompt || '');
    // Remount the canvas so it fits the loaded graph.
    setCanvasResetKey(n => n + 1);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [catalogData, integrationsData] = await Promise.all([
        apiClient.getWorkflowCatalog(),
        apiClient.getWorkflowIntegrationsStatus(),
      ]);
      setCatalog(catalogData);
      setIntegrations(integrationsData);

      // Nothing connected yet: offer the simulated environment so the customer
      // can still build and test workflows. Only for fresh builder visits — skip
      // when editing an existing workflow or when the builder was already opened
      // in simulated mode (e.g. from the onboarding wizard).
      const anyConnected = hasRealIntegration(integrationsData);
      if (!id && !simulated && !anyConnected) {
        setShowSimulatedPrompt(true);
      }

      // Use catalog data directly (already fetched in parallel by the server)
      if (catalogData.github_repos) setGithubRepos(catalogData.github_repos);
      if (catalogData.gitlab_repos) setGitlabRepos(catalogData.gitlab_repos);
      if (catalogData.confluence_spaces) setConfluenceSpaces(catalogData.confluence_spaces);
      if (catalogData.jira_projects) setJiraProjects(catalogData.jira_projects);
      if (catalogData.linear_teams) setLinearTeams(catalogData.linear_teams.map((t: any) => ({ key: t.key, name: t.name })));
      if (catalogData.vercel_projects) setVercelProjects(catalogData.vercel_projects.map((p: any) => ({ key: p.id, name: p.name })));
      if (catalogData.railway_projects) setRailwayProjects(catalogData.railway_projects.map((p: any) => ({ key: p.id, name: p.name })));
      if (catalogData.flyio_apps) setFlyioApps(catalogData.flyio_apps.map((a: any) => ({ key: a.id, name: a.name })));
      if (catalogData.railway_services) setRailwayServices(catalogData.railway_services.map((s: any) => ({ key: s.id, name: s.name })));
      if (catalogData.railway_environments) setRailwayEnvironments(catalogData.railway_environments.map((e: any) => ({ key: e.id, name: e.name })));
      if (catalogData.clusters) setClusters(catalogData.clusters.map((c: any) => ({ id: c.id, name: c.name })));
      if (catalogData.aws_accounts) setAwsAccounts(catalogData.aws_accounts.map((a: any) => ({ key: a.account_id || a.id, name: a.account_id || a.id })));
      if (catalogData.gcp_projects) setGcpProjects(catalogData.gcp_projects.map((p: any) => ({ key: p.key, name: p.name })));

      // Nebius projects/clusters are not part of the catalog payload; fetch them
      // directly so the dynamic dropdowns are populated when Nebius is connected.
      // The public demo builder skips all tenant-scoped dropdown fetchers —
      // integrations are only simulated as connected, so there is no data.
      const realIntegrations = IS_DEMO_MODE ? [] : (integrationsData || []);
      if (!IS_DEMO_MODE) {
        apiClient.getNebiusProjects().then(r => setNebiusProjects(r || [])).catch(() => setNebiusProjects([]));
        apiClient.getNebiusClusters().then(r => setNebiusClusters(r || [])).catch(() => setNebiusClusters([]));
        // Beam deployments/containers/machines are not part of the catalog payload;
        // fetch them directly so the dynamic dropdowns are populated when Beam is connected.
        apiClient.getBeamDeployments().then(r => setBeamDeployments(r || [])).catch(() => setBeamDeployments([]));
        apiClient.getBeamContainers().then(r => setBeamContainers(r || [])).catch(() => setBeamContainers([]));
        apiClient.getBeamMachines().then(r => setBeamMachines(r || [])).catch(() => setBeamMachines([]));
        apiClient.getBeamSandboxes().then(r => setBeamSandboxes(r || [])).catch(() => setBeamSandboxes([]));
      }
      // Daytona sandboxes/snapshots/volumes — only fetch when Daytona is
      // connected, so disconnected tenants don't pay for (or wait on) extra
      // requests on every builder load.
      const daytonaConnected = realIntegrations.some((i: any) => i.id === 'daytona' && i.connected);
      if (daytonaConnected) {
        apiClient.getDaytonaSandboxes().then(r => setDaytonaSandboxes(r || [])).catch(() => setDaytonaSandboxes([]));
        apiClient.getDaytonaSnapshots().then(r => setDaytonaSnapshots(r || [])).catch(() => setDaytonaSnapshots([]));
        apiClient.getDaytonaVolumes().then(r => setDaytonaVolumes(r || [])).catch(() => setDaytonaVolumes([]));
        apiClient.getDaytonaTargets().then(r => setDaytonaTargets(r || [])).catch(() => setDaytonaTargets([]));
      }
      // Supabase projects/branches — only fetch when Supabase is connected.
      const supabaseConnected = realIntegrations.some((i: any) => i.id === 'supabase' && i.connected);
      if (supabaseConnected) {
        apiClient.getSupabaseProjects().then(r => setSupabaseProjects(r || [])).catch(() => setSupabaseProjects([]));
        apiClient.getSupabaseBranches().then(r => setSupabaseBranches(r || [])).catch(() => setSupabaseBranches([]));
      }
      // PlanetScale databases/branches/deploy-requests — only fetch when connected.
      const planetscaleConnected = realIntegrations.some((i: any) => i.id === 'planetscale' && i.connected);
      if (planetscaleConnected) {
        apiClient.getPlanetScaleDatabases().then(r => setPlanetscaleDatabases(r || [])).catch(() => setPlanetscaleDatabases([]));
        apiClient.getPlanetScaleBranches().then(r => setPlanetscaleBranches(r || [])).catch(() => setPlanetscaleBranches([]));
        apiClient.getPlanetScaleDeployRequests().then(r => setPlanetscaleDeployRequests(r || [])).catch(() => setPlanetscaleDeployRequests([]));
      }
      // Neon projects/branches/endpoints — only fetch when connected.
      const neonConnected = realIntegrations.some((i: any) => i.id === 'neon' && i.connected);
      if (neonConnected) {
        apiClient.getNeonProjects().then(r => setNeonProjects(r || [])).catch(() => setNeonProjects([]));
        apiClient.getNeonBranches().then(r => setNeonBranches(r || [])).catch(() => setNeonBranches([]));
        apiClient.getNeonEndpoints().then(r => setNeonEndpoints(r || [])).catch(() => setNeonEndpoints([]));
      }

      // ClickHouse services/clickpipes/backups — only fetch when connected.
      const clickhouseConnected = realIntegrations.some((i: any) => i.id === 'clickhouse' && i.connected);
      if (clickhouseConnected) {
        apiClient.getClickHouseServices().then(r => setClickHouseServices(r || [])).catch(() => setClickHouseServices([]));
        apiClient.getClickHouseClickPipes().then(r => setClickHouseClickPipes(r || [])).catch(() => setClickHouseClickPipes([]));
        apiClient.getClickHouseBackups().then(r => setClickHouseBackups(r || [])).catch(() => setClickHouseBackups([]));
      }

      // Jenkins jobs — only fetch when connected.
      const jenkinsConnected = realIntegrations.some((i: any) => i.id === 'jenkins' && i.connected);
      if (jenkinsConnected) {
        apiClient.getJenkinsJobs().then(r => setJenkinsJobs(r || [])).catch(() => setJenkinsJobs([]));
      }

      // CircleCI projects — only fetch when connected. Pipelines are fetched
      // in NodeConfigPanel because they depend on the selected project.
      const circleciConnected = realIntegrations.some((i: any) => i.id === 'circleci' && i.connected);
      if (circleciConnected) {
        apiClient.getCircleCIProjects().then(r => setCircleCIProjects(r || [])).catch(() => setCircleCIProjects([]));
      }

      // SonarCloud projects — only fetch when connected.
      const sonarcloudConnected = realIntegrations.some((i: any) => i.id === 'sonarcloud' && i.connected);
      if (sonarcloudConnected) {
        apiClient.getSonarCloudProjects().then(r => setSonarCloudProjects(r || [])).catch(() => setSonarCloudProjects([]));
      }

      // Okta users/groups — only fetch when connected.
      const oktaConnected = realIntegrations.some((i: any) => i.id === 'okta' && i.connected);
      if (oktaConnected) {
        apiClient.getOktaUsers().then(r => setOktaUsers(r || [])).catch(() => setOktaUsers([]));
        apiClient.getOktaGroups().then(r => setOktaGroups(r || [])).catch(() => setOktaGroups([]));
      }

      // Terraform Cloud workspaces — only fetch when connected. Runs are fetched
      // in NodeConfigPanel because they depend on the selected workspace.
      const terraformConnected = realIntegrations.some((i: any) => i.id === 'terraform' && i.connected);
      if (terraformConnected) {
        apiClient.getTerraformWorkspaces().then(r => setTerraformWorkspaces(r || [])).catch(() => setTerraformWorkspaces([]));
      }

      // Databricks jobs/clusters/pipelines/warehouses — only fetch when connected.
      const databricksConnected = realIntegrations.some((i: any) => i.id === 'databricks' && i.connected);
      if (databricksConnected) {
        apiClient.getDatabricksJobs().then(r => setDatabricksJobs(r || [])).catch(() => setDatabricksJobs([]));
        apiClient.getDatabricksClusters().then(r => setDatabricksClusters(r || [])).catch(() => setDatabricksClusters([]));
        apiClient.getDatabricksPipelines().then(r => setDatabricksPipelines(r || [])).catch(() => setDatabricksPipelines([]));
        apiClient.getDatabricksWarehouses().then(r => setDatabricksWarehouses(r || [])).catch(() => setDatabricksWarehouses([]));
      }

      // Pulumi Cloud stacks — only fetch when connected.
      const pulumiConnected = realIntegrations.some((i: any) => i.id === 'pulumi' && i.connected);
      if (pulumiConnected) {
        apiClient.getPulumiStacks().then(r => setPulumiStacks(r || [])).catch(() => setPulumiStacks([]));
      }

      // Vault mounts/policies/database-mounts — only fetch when connected.
      // Secret paths and static roles are fetched in NodeConfigPanel because
      // they depend on the selected mount.
      const vaultConnected = realIntegrations.some((i: any) => i.id === 'vault' && i.connected);
      if (vaultConnected) {
        apiClient.getVaultMounts().then(r => setVaultMounts(r || [])).catch(() => setVaultMounts([]));
        apiClient.getVaultPolicies().then(r => setVaultPolicies(r || [])).catch(() => setVaultPolicies([]));
        apiClient.getVaultDatabaseMounts().then(r => setVaultDatabaseMounts(r || [])).catch(() => setVaultDatabaseMounts([]));
      }

      // Infisical projects/secret-syncs — only fetch when connected.
      // Environments and folders are fetched in NodeConfigPanel because they
      // depend on the selected project.
      const infisicalConnected = realIntegrations.some((i: any) => i.id === 'infisical' && i.connected);
      if (infisicalConnected) {
        apiClient.getInfisicalProjects().then(r => setInfisicalProjects(r || [])).catch(() => setInfisicalProjects([]));
        apiClient.getInfisicalSecretSyncs().then(r => setInfisicalSecretSyncs(r || [])).catch(() => setInfisicalSecretSyncs([]));
      }

      if (id) {
        const wf = await apiClient.getWorkflow(id);
        setWorkflow(wf);
        setName(wf.name);
        if (wf.simulated) setSimulated(true);
        setDescription(wf.description || 'Describe what this workflow does...');
        const loadedDef = wf.definition || EMPTY_DEFINITION;
        const loadedTC = wf.trigger_config || triggerConfig;
        if (loadedTC?.signals?.[0]?.filters) {
          const triggerNode = loadedDef.nodes.find((n: any) => n.type === 'trigger');
          if (triggerNode) {
            triggerNode.data = { ...triggerNode.data, signal_filter: loadedTC.signals[0].filters };
          }
        }
        setDefinition(loadedDef);
        setTriggerConfig(loadedTC);
        if (wf.nl_prompt) setNLPrompt(wf.nl_prompt);
        if (wf.alert_config) {
          try {
            const ac = typeof wf.alert_config === 'string' ? JSON.parse(wf.alert_config) : wf.alert_config;
            setAlertConfig({
              enabled: ac.enabled || false,
              channels: ac.channels || (ac.channel ? [ac.channel] : []),
              dm_user_ids: ac.dm_user_ids || (ac.dm_user_id ? [ac.dm_user_id] : []),
            });
          } catch { /* ignore parse errors */ }
        }
      } else if (IS_DEMO_MODE && searchParams.get('share')) {
        // Shared demo workflow (demo.usekestrel.ai/w/{id}): hydrate the
        // builder from the public snapshot.
        try {
          const shared = await apiClient.demoGetSharedWorkflow(searchParams.get('share')!);
          applySharedSnapshot(shared);
        } catch {
          setError('This shared workflow link is invalid or no longer available.');
        }
      } else if (IS_DEMO_MODE && searchParams.get('bundle')) {
        // Shared bundle (demo.usekestrel.ai/b/{id}): load every snapshot and
        // show the first, with a picker to switch between them.
        try {
          const bundle = await apiClient.demoGetSharedBundle(searchParams.get('bundle')!);
          if (!bundle.workflows?.length) throw new Error('empty bundle');
          setBundleWorkflows(bundle.workflows);
          setBundleIndex(0);
          applySharedSnapshot(bundle.workflows[0]);
        } catch {
          setError('This shared bundle link is invalid or no longer available.');
        }
      } else if ((location.state as any)?.suggestion) {
        const s = (location.state as any).suggestion;
        if (s.name) setName(s.name);
        if (s.description) setDescription(s.description);
        if (s.definition) setDefinition(s.definition);
        if (s.trigger_config) setTriggerConfig(s.trigger_config);
        if (s.nl_prompt) setNLPrompt(s.nl_prompt);
        if ((location.state as any)?.fromRequest) {
          fromRequestIdRef.current = (location.state as any).fromRequest;
        }
        if (s.from_suggestion_id) {
          fromSuggestionIdRef.current = s.from_suggestion_id;
        }
        // Clear location state so refreshing doesn't re-apply
        window.history.replaceState({}, document.title);
      }

      // Apply suggested definition from diagnosis chat (after workflow loads)
      if ((location.state as any)?.suggestedDefinition) {
        const suggested = (location.state as any).suggestedDefinition;
        if (suggested.nodes && suggested.edges) {
          setDefinition(suggested);
        } else if (suggested.definition?.nodes) {
          setDefinition(suggested.definition);
          if (suggested.trigger_config) setTriggerConfig(suggested.trigger_config);
          if (suggested.name) setName(suggested.name);
          if (suggested.description) setDescription(suggested.description);
        }
        window.history.replaceState({}, document.title);
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  // The trigger node's source/signal/filters are the source of truth; the
  // page-level triggerConfig contributes cooldown/concurrency. Shared by
  // Save and the demo Test run.
  const buildEffectiveTriggerConfig = (def: WorkflowDefinition) => {
    const triggerNode = def.nodes.find(n => n.type === 'trigger');
    return triggerNode
      ? {
          source: (triggerNode.data.source || 'kubernetes') as string,
          signals: [{ signal_type: triggerNode.data.signal_type || 'any', filters: triggerNode.data.signal_filter || {} }],
          cooldown_hours: triggerConfig.cooldown_hours || 0,
          cooldown_minutes: triggerConfig.cooldown_minutes || 0,
          no_cooldown: triggerConfig.no_cooldown || false,
          max_concurrent_executions: triggerConfig.max_concurrent_executions || 3,
        }
      : triggerConfig;
  };

  const handleSave = async () => {
    // Public demo builder: nothing is persisted for the visitor — saving
    // routes to signup instead.
    if (IS_DEMO_MODE) {
      setDemoSignupCTA('save');
      return;
    }
    if (!name.trim() || name === 'Untitled Workflow') {
      setEditingName(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Use the enriched definition for saving so custom block metadata
      // (integration_name, custom_icon_url, etc.) is persisted
      const definitionToSave = enrichedDefinition;
      const effectiveTriggerConfig = buildEffectiveTriggerConfig(definitionToSave);

      const desc = description === 'Describe what this workflow does...' ? '' : description;

      if (workflow) {
        await apiClient.updateWorkflow(workflow.id, { name, description: desc, definition: definitionToSave, trigger_config: effectiveTriggerConfig, nl_prompt: nlPrompt, alert_config: alertConfig.enabled ? alertConfig : null });
      } else {
        const created = await apiClient.createWorkflow({ name, description: desc, definition: definitionToSave, trigger_config: effectiveTriggerConfig, nl_prompt: nlPrompt, alert_config: alertConfig.enabled ? alertConfig : null, from_suggestion_id: fromSuggestionIdRef.current || undefined, simulated: simulated || undefined });
        // Carry the draft builder-chat session over to the saved workflow's
        // id so the creation conversation stays attached to the workflow
        // (one session per workflow). Fire-and-forget: a failure only means
        // the chat starts fresh on the edit page.
        if (!IS_DEMO_MODE) {
          const draftCtx = draftChatContextRef.current;
          apiClient.listChatSessions(false, 'workflow_suggestions', draftCtx)
            .then(r => {
              const session = (r.sessions || []).filter(s => !s.deleted_at)[0];
              if (session) {
                return apiClient.updateChatSession(session.id, { context_id: `workflow-builder-${created.id}` });
              }
            })
            .catch(() => {});
        }
        // If this workflow was created from a workflow request, mark the request as approved
        if (fromRequestIdRef.current) {
          try {
            await apiClient.workflowRequestAction(fromRequestIdRef.current, 'approve');
          } catch {
            // non-fatal — the workflow was created successfully
          }
          fromRequestIdRef.current = null;
        }
        navigate(`/workflows/${created.id}/edit`, { replace: true });
      }
      // Regenerate code panel content if it's open
      if (showCodePanel) {
        setCodePanelRegenerateTrigger(n => n + 1);
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to save workflow'));
    } finally {
      setSaving(false);
    }
  };

  const validateWorkflow = (): string | null => {
    if (definition.nodes.length === 0) return 'Workflow has no nodes. Add at least a trigger and one action.';
    if (!definition.nodes.some(n => n.type === 'trigger')) return 'Workflow has no trigger node.';
    for (const node of definition.nodes) {
      if (node.type !== 'action') continue;
      const integration = node.data?.integration;
      const action = node.data?.action;
      const config = node.data?.config || {};
      const repo = config.repo;
      const project = config.project;

      // GitHub repo validation
      if ((integration === 'github') && (action === 'github-create-pr' || action === 'github-create-issue')) {
        if (!repo || repo === 'auto') {
          if (githubRepos.length === 0) return `Node "${node.data?.label || node.id}": No GitHub repository selected and no repos connected.`;
        } else if (!repo.includes('{{') && !githubRepos.includes(repo)) {
          return `Node "${node.data?.label || node.id}": Repository "${repo}" is not in your connected GitHub repos.`;
        }
      }
      // GitLab project validation
      if ((integration === 'gitlab') && (action === 'gitlab-create-mr' || action === 'gitlab-create-issue')) {
        if (!project || project === 'auto') {
          if (gitlabRepos.length === 0) return `Node "${node.data?.label || node.id}": No GitLab project selected and no projects connected.`;
        } else if (!gitlabRepos.includes(project)) {
          return `Node "${node.data?.label || node.id}": Project "${project}" is not in your connected GitLab projects.`;
        }
      }

      // Required fields validation for all action blocks
      if (action && catalog?.actions) {
        const template = catalog.actions.find(t => t.id === action);
        if (template) {
          for (const field of template.fields) {
            if (!field.required) continue;
            const value = config[field.name];
            // Skip if the value is a template variable (will be resolved at runtime)
            if (typeof value === 'string' && value.includes('{{')) continue;
            // Skip boolean fields (they always have a value)
            if (field.type === 'boolean') continue;
            // Check if the required field is empty
            if (value === undefined || value === null || value === '') {
              return `Node "${node.data?.label || node.id}": Required field "${field.label}" is not set.`;
            }
          }
        }
      }
    }
    return null;
  };

  const handleActivate = async () => {
    if (!workflow) return;
    const validationError = validateWorkflow();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      // Auto-save before activating to ensure latest changes are persisted
      await handleSave();
      await apiClient.activateWorkflow(workflow.id);
      setWorkflow({ ...workflow, status: 'active' });
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to activate'));
    }
  };

  const handlePause = async () => {
    if (!workflow) return;
    try {
      await apiClient.pauseWorkflow(workflow.id);
      setWorkflow({ ...workflow, status: 'paused' });
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to pause'));
    }
  };

  // Public demo builder: publish the canvas as an anonymous shareable
  // snapshot and surface the demo.usekestrel.ai/w/{id} link.
  const handleDemoShare = async () => {
    if (definition.nodes.length === 0 || !definition.nodes.some(n => n.type === 'trigger')) {
      setError('Add a trigger and at least one action before sharing.');
      return;
    }
    setDemoSharing(true);
    setError(null);
    try {
      const desc = description === 'Describe what this workflow does...' ? '' : description;
      const result = await apiClient.demoShareWorkflow({
        name,
        description: desc,
        definition: enrichedDefinition,
        trigger_config: buildEffectiveTriggerConfig(enrichedDefinition),
        nl_prompt: nlPrompt || undefined,
      });
      setDemoShareUrl(`${window.location.origin}/w/${result.id}`);
      // Track shares made this session so they can be bundled into one link.
      setSessionShareIds(prev => {
        const next = prev.includes(result.id) ? prev : [...prev, result.id];
        try {
          sessionStorage.setItem('demo_share_session_ids', JSON.stringify(next));
        } catch { /* storage unavailable */ }
        return next;
      });
      setDemoBundleUrl('');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError('You\u2019re sharing too quickly \u2014 wait a moment and try again.');
      } else {
        setError(getErrorMessage(err, 'Failed to create the share link'));
      }
    } finally {
      setDemoSharing(false);
    }
  };

  // Collapse every workflow shared this session into one /b/{id} link.
  const handleDemoBundle = async () => {
    if (sessionShareIds.length < 2) return;
    setDemoBundleCreating(true);
    try {
      const result = await apiClient.demoShareBundle(sessionShareIds);
      setDemoBundleUrl(`${window.location.origin}/b/${result.id}`);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError('You\u2019re sharing too quickly \u2014 wait a moment and try again.');
      } else {
        setError(getErrorMessage(err, 'Failed to create the bundle link'));
      }
    } finally {
      setDemoBundleCreating(false);
    }
  };

  // Mobile tap-to-add from the Blocks drawer: append the block to the
  // definition (staggered grid placement), close the drawer, and remount the
  // canvas so it re-fits around the new node. Desktop drag-and-drop is
  // untouched — the sidebar only invokes this below the md breakpoint.
  const handleMobileBlockAdd = useCallback((nodeType: string, data: any) => {
    setDefinition(prev => {
      const idx = prev.nodes.length;
      const node: WorkflowNode = {
        id: `${nodeType}-${Date.now()}`,
        // The sidebar's drag payload only ever carries 'trigger' | 'action'.
        type: nodeType as WorkflowNode['type'],
        position: { x: 60 + (idx % 3) * 260, y: 120 + Math.floor(idx / 3) * 140 },
        data,
      };
      return { ...prev, nodes: [...prev.nodes, node] };
    });
    setMobileSidebarOpen(false);
    setCanvasResetKey(n => n + 1);
  }, []);

  const handleNLGenerated = useCallback(
    (def: WorkflowDefinition, tc: any, generatedName: string, generatedDesc: string, prompt: string) => {
      // Mirror the trigger_config filters onto the trigger node, exactly like
      // loadData does for saved workflows: the config panel reads the node's
      // data.signal_filter, so agent-supplied scoping (accounts, clusters,
      // namespaces...) must land there even when the agent only set it in
      // trigger_config.
      if (tc?.signals?.[0]?.filters) {
        const triggerNode = def.nodes.find((n: any) => n.type === 'trigger');
        if (triggerNode) {
          triggerNode.data = {
            ...triggerNode.data,
            signal_filter: { ...(triggerNode.data.signal_filter || {}), ...tc.signals[0].filters },
          };
        }
      }
      setDefinition(def);
      if (tc) setTriggerConfig(tc);
      if (generatedName) setName(generatedName);
      if (generatedDesc) setDescription(generatedDesc);
      if (prompt) setNLPrompt(prompt);
      // Remount the canvas so it re-fits the view around the generated graph
      // (fitView only runs on mount).
      setCanvasResetKey(n => n + 1);
      // Kick off SDK-code generation and close the node config panel to give the
      // canvas space. The demo builder skips the code panel entirely (its
      // SDK-code stream is a second LLM call and an authenticated endpoint).
      //
      // On mobile the panel is generated but left closed: it would otherwise
      // cover nearly the whole screen and bury the workflow that was just
      // generated. The panel stays mounted behind `display: none`, so the
      // stream still runs and the code is ready the moment Code is tapped.
      setSelectedNodeId(null);
      if (!IS_DEMO_MODE) {
        if (!isMobileViewport()) {
          setShowCodePanel(true);
        }
        setCodePanelRegenerateTrigger(n => n + 1);
      }
    },
    [],
  );

  // Conversational builder chat (non-demo): keep the latest canvas serialized
  // in a ref so each chat message carries it — the agent then modifies the
  // existing workflow instead of regenerating from scratch.
  const [builderChatOpen, setBuilderChatOpen] = useState(false);
  // Default panel width; drag-resizable so users can trade chat space for
  // canvas space. Clamped on resize below.
  const [chatPanelWidth, setChatPanelWidth] = useState(500);
  const isResizingChat = useRef(false);
  // One chat session per workflow: an unsaved workflow gets a stable draft
  // context for this builder mount; on first save the session is re-pointed
  // at the workflow id so its creation history stays attached.
  const draftChatContextRef = useRef(
    `workflow-builder-draft-${(typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`
  );
  const builderChatContextId = id ? `workflow-builder-${id}` : draftChatContextRef.current;
  // Workflows created before the conversational builder agent shipped were
  // built with the one-shot Workflow Agent and have no chat history — keep
  // them on the old NL input so they look and behave exactly as they used to.
  // Adjust this cutoff to the production deploy time of the builder chat.
  const CONVERSATIONAL_BUILDER_CUTOFF = '2026-09-06T00:00:00Z';
  const usesLegacyNLInput = !IS_DEMO_MODE && !!id && !!workflow?.created_at &&
    new Date(workflow.created_at) < new Date(CONVERSATIONAL_BUILDER_CUTOFF);

  const currentWorkflowRef = useRef<any>(null);
  useEffect(() => {
    currentWorkflowRef.current = definition?.nodes?.length
      ? { name, description, trigger_config: triggerConfig, definition }
      : null;
  }, [definition, triggerConfig, name, description]);

  // Drag-resize for the chat panel (desktop only; mobile is full-width).
  // Identical styling/behavior to the Library page's assistant resize handle:
  // an invisible 9px hit area whose inner 3px line fades in on hover and
  // highlights while dragging.
  const resizeChatLineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizingChat.current) return;
      const newWidth = Math.max(380, Math.min(Math.round(window.innerWidth * 0.4), window.innerWidth - e.clientX));
      setChatPanelWidth(newWidth);
    };
    const onMouseUp = () => {
      if (isResizingChat.current) {
        isResizingChat.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (resizeChatLineRef.current) {
          resizeChatLineRef.current.style.transitionDelay = '0ms';
          resizeChatLineRef.current.style.backgroundColor = 'transparent';
        }
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);
  const startChatResize = useCallback(() => {
    isResizingChat.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (resizeChatLineRef.current) {
      resizeChatLineRef.current.style.transitionDelay = '0ms';
      resizeChatLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.5)';
    }
  }, []);

  const handleChatWorkflowGenerated = useCallback(
    (wf: any) => {
      if (!wf?.definition?.nodes?.length) return;
      handleNLGenerated(
        wf.definition,
        wf.trigger_config || null,
        wf.name || '',
        wf.description || '',
        wf.description || '',
      );
      // Hand focus to the canvas: the chat's own render of the workflow is a
      // compact chip, and closing the panel completes the transition. The
      // conversation survives (the panel is CSS-hidden), so reopening the
      // pill continues refining the same workflow.
      setBuilderChatOpen(false);
      toast.success('Workflow built on the canvas — reopen the Workflow Agent to keep refining it', { duration: 4000 });
    },
    [handleNLGenerated],
  );

  const handleBrowseSuggestions = useCallback(() => {
    // With nothing connected there is no basis for real suggestions, so open the
    // Library with the simulated picker already up rather than landing the
    // customer on a page whose primary action cannot help them. The builder
    // already has integration status in state, so this needs no extra fetch.
    navigate('/workflows/suggestions', {
      state: hasRealIntegration(integrations) ? undefined : { openSimulatedPicker: true },
    });
  }, [navigate, integrations]);

  const handleNodeUpdate = useCallback(
    (nodeId: string, data: any) => {
      setDefinition(prev => ({
        ...prev,
        nodes: prev.nodes.map(n => (n.id === nodeId ? { ...n, data } : n)),
      }));
    },
    [],
  );

  const selectedNode = enrichedDefinition.nodes.find(n => n.id === selectedNodeId) || null;

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Page header — matches the standard PageHeader layout exactly */}
      <div className="bg-background shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex max-md:flex-col max-md:items-stretch max-md:gap-3 md:items-center justify-between">
            <div className="flex-1 min-w-0 pr-4 flex items-start gap-1">
              <MobileMenuButton />
              <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="h-9 w-full max-w-80 text-2xl font-bold"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') setEditingName(false); }}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setEditingName(false)} className="h-8 w-8 p-0">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group cursor-pointer" onClick={() => setEditingName(true)}>
                  <h1 className="text-2xl font-bold text-foreground">{name}</h1>
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
              {editingDesc ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    className="h-7 w-full max-w-[480px] text-sm"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') setEditingDesc(false); }}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setEditingDesc(false)} className="h-6 w-6 p-0">
                    <Check className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 group cursor-pointer mt-1" onClick={() => setEditingDesc(true)}>
                  <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
                  <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </div>
              )}
              <div className="flex items-center gap-2 mt-2 max-md:flex-wrap">
                <Clock className="w-3.5 h-3.5 text-muted-foreground max-md:hidden" />
                <span className="text-xs text-muted-foreground">Cooldown:</span>
                {(() => {
                  const triggerNode = definition.nodes.find(n => n.type === 'trigger');
                  const source = triggerNode?.data?.source || triggerConfig.source || 'kubernetes';
                  const hasExplicitCooldown = (triggerConfig.cooldown_hours != null && triggerConfig.cooldown_hours > 0) || (triggerConfig.cooldown_minutes != null && triggerConfig.cooldown_minutes > 0);
                  const isNoCooldown = triggerConfig.cooldown_hours === 0 && triggerConfig.cooldown_minutes === 0 && triggerConfig.no_cooldown;
                  const totalMinutes = (triggerConfig.cooldown_hours || 0) * 60 + (triggerConfig.cooldown_minutes || 0);
                  const displayValue = isNoCooldown ? '0' : (
                    hasExplicitCooldown ? String(totalMinutes) : (
                      ['kubernetes', 'aws', 'pagerduty'].includes(source) ? '1440' : '10'
                    )
                  );
                  return (
                    <>
                      <Select
                        value={displayValue}
                        onValueChange={v => {
                          const mins = parseInt(v, 10);
                          if (mins === 0) {
                            setTriggerConfig((prev: any) => ({ ...prev, cooldown_hours: 0, cooldown_minutes: 0, no_cooldown: true }));
                          } else if (mins >= 60) {
                            setTriggerConfig((prev: any) => ({ ...prev, cooldown_hours: Math.floor(mins / 60), cooldown_minutes: 0, no_cooldown: false }));
                          } else {
                            setTriggerConfig((prev: any) => ({ ...prev, cooldown_hours: 0, cooldown_minutes: mins, no_cooldown: false }));
                          }
                        }}
                      >
                        <SelectTrigger className="h-7 w-[140px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">No cooldown</SelectItem>
                          <SelectItem value="5">Every 5 mins</SelectItem>
                          <SelectItem value="10">Every 10 mins</SelectItem>
                          <SelectItem value="15">Every 15 mins</SelectItem>
                          <SelectItem value="30">Every 30 mins</SelectItem>
                          <SelectItem value="60">Every 1 hour</SelectItem>
                          <SelectItem value="180">Every 3 hours</SelectItem>
                          <SelectItem value="720">Every 12 hours</SelectItem>
                          <SelectItem value="1440">Every 24 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </>
                  );
                })()}

                <div className="w-px h-4 bg-border mx-1" />

                {!IS_DEMO_MODE && (
                  <>
                    <Bell className="w-3.5 h-3.5 text-muted-foreground max-md:hidden" />
                <span className="text-xs text-muted-foreground">Failure alerts:</span>
                <Switch
                  checked={alertConfig.enabled}
                  onCheckedChange={v => setAlertConfig(prev => ({ ...prev, enabled: v }))}
                  className="scale-75"
                />
                {alertConfig.enabled && (
                  <>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2">
                          {alertConfig.channels.length + alertConfig.dm_user_ids.length === 0
                            ? 'Select recipients'
                            : `${alertConfig.channels.length + alertConfig.dm_user_ids.length} selected`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-0" align="start">
                        <div className="max-h-[300px] overflow-y-auto">
                          {(catalog?.slack_channels || []).length > 0 && (
                            <div className="p-2">
                              <div className="text-xs font-medium text-muted-foreground px-1 pb-1">Channels</div>
                              {(catalog?.slack_channels || []).map((ch: any) => {
                                const selected = alertConfig.channels.includes(ch.id);
                                return (
                                  <button
                                    key={ch.id}
                                    className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${selected ? 'bg-accent' : ''}`}
                                    onClick={() => setAlertConfig(prev => ({
                                      ...prev,
                                      channels: selected
                                        ? prev.channels.filter(c => c !== ch.id)
                                        : [...prev.channels, ch.id],
                                    }))}
                                  >
                                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                      {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                                    </div>
                                    #{ch.name || ch.id}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {(catalog?.slack_users || []).length > 0 && (
                            <div className="p-2 border-t">
                              <div className="text-xs font-medium text-muted-foreground px-1 pb-1">DM Users</div>
                              {(catalog?.slack_users || []).map((u: any) => {
                                const selected = alertConfig.dm_user_ids.includes(u.id);
                                return (
                                  <button
                                    key={u.id}
                                    className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${selected ? 'bg-accent' : ''}`}
                                    onClick={() => setAlertConfig(prev => ({
                                      ...prev,
                                      dm_user_ids: selected
                                        ? prev.dm_user_ids.filter(id => id !== u.id)
                                        : [...prev.dm_user_ids, u.id],
                                    }))}
                                  >
                                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                      {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                                    </div>
                                    {u.real_name || u.name || u.id}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {(catalog?.slack_channels || []).length === 0 && (catalog?.slack_users || []).length === 0 && (
                            <div className="p-4 text-xs text-muted-foreground text-center">
                              Connect Slack to enable failure alerts
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </>
                )}

                <div className="w-px h-4 bg-border mx-1" />

                <div
                  className="flex items-center gap-2"
                  title={
                    workflow?.simulated
                      ? 'This workflow was saved as simulated and cannot be converted to a real workflow.'
                      : 'Simulated mode: build and test workflows as if every integration were connected. Saved workflows are marked simulated and cannot be activated.'
                  }
                >
                  <FlaskConical className="w-3.5 h-3.5 text-muted-foreground max-md:hidden" />
                  <span className="text-xs text-muted-foreground">Simulated:</span>
                  <Switch
                    checked={simulated}
                    onCheckedChange={setSimulated}
                    disabled={!!workflow?.simulated}
                    className="scale-75"
                  />
                </div>

                <div className="w-px h-4 bg-border mx-1" />
                  </>
                )}

                <span className="text-xs text-muted-foreground font-mono">${computedCostPerRun.toFixed(2)}/run</span>

                {/* Escape hatch for the blank-canvas problem: a customer who
                    opens the builder without knowing what to automate has
                    nothing to act on. Only for a fresh, untouched workflow —
                    adding a node or editing a saved one means the intent is
                    already known. No Library route in the public demo. */}
                {!IS_DEMO_MODE && !id && definition.nodes.length === 0 && (
                  <>
                    <div className="w-px h-4 bg-border mx-1 max-md:hidden" />
                    <button
                      onClick={handleBrowseSuggestions}
                      className="suggest-btn relative inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium text-foreground"
                      title="Browse AI-suggested workflows for your integrations"
                    >
                      <span className="suggest-btn-border" />
                      <span className="suggest-btn-bg" />
                      <span className="relative z-10 flex items-center gap-1.5 whitespace-nowrap">
                        <Sparkles className="w-3.5 h-3.5 shrink-0" />
                        <span className="max-lg:hidden">Not sure what to automate? </span>
                        Browse suggestions
                      </span>
                    </button>
                  </>
                )}
              </div>
              </div>
            </div>
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-end gap-2 shrink-0">
              {workflow && workflow.status !== 'active' && !simulated && (
                <Button size="sm" onClick={handleActivate} className="gap-1.5">
                  <Play className="w-3.5 h-3.5" /> Activate
                </Button>
              )}
              {workflow && workflow.status === 'active' && (
                <Button variant="outline" size="sm" onClick={handlePause} className="gap-1.5">
                  <Pause className="w-3.5 h-3.5" /> Pause
                </Button>
              )}
              {IS_DEMO_MODE && (
                <>
                  <Button
                    size="sm"
                    onClick={() => setDemoSignupCTA('test')}
                    className="gap-1.5"
                    title="Sign up to test this workflow"
                  >
                    <Play className="w-3.5 h-3.5" /> Test
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDemoShare}
                    disabled={demoSharing}
                    className="gap-1.5"
                    title="Get a link anyone can open in the demo builder"
                  >
                    {demoSharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                    Share
                  </Button>
                </>
              )}
              {!IS_DEMO_MODE && (
                <Button
                  variant={showCodePanel ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowCodePanel(v => {
                      if (!v) setSelectedNodeId(null);
                      return !v;
                    });
                  }}
                  className="gap-1.5"
                  title="View Python SDK code"
                >
                  <Code2 className="w-3.5 h-3.5" /> Code
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </Button>
              <ModeToggle />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300" style={{ marginLeft: IS_DEMO_MODE ? '0px' : '128px' }}>
          <div
            className="flex items-start gap-2.5 max-w-lg rounded-lg border border-destructive/40 bg-popover/95 backdrop-blur-sm shadow-lg px-3.5 py-2.5 text-sm text-foreground"
            role="alert"
          >
            <span className="leading-relaxed">{error}</span>
            <button
              onClick={() => setError(null)}
              className="shrink-0 mt-0.5 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Main content: sidebar + canvas + config panel — all within a single non-scrolling row */}
      <div className="flex min-h-0 flex-1 border-t">
        {catalog && (
          <>
          {/* Mobile: the sidebar becomes a slide-over drawer with a backdrop. */}
          {mobileSidebarOpen && (
            <div
              className="md:hidden fixed inset-0 z-30 bg-black/50"
              onClick={() => setMobileSidebarOpen(false)}
              aria-hidden="true"
            />
          )}
          <div
            className={`shrink-0 overflow-y-auto border-r scrollbar-hide w-64 max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-40 max-md:w-72 max-md:bg-background max-md:shadow-xl ${mobileSidebarOpen ? '' : 'max-md:hidden'}`}
            style={{ scrollbarWidth: 'none' }}
          >
            <WorkflowSidebar
              signals={catalog.signals}
              actions={catalog.actions}
              integrations={effectiveIntegrations}
              customBlocks={catalog.custom_blocks}
              onBlockAdd={handleMobileBlockAdd}
              onCreateCustomBlock={() => setShowCreateCustomBlock(true)}
              onDeleteCustomBlock={(blockId) => {
                if (IS_DEMO_MODE) {
                  // Demo blocks are client-side only — drop from local state.
                  setCatalog(prev => prev ? { ...prev, custom_blocks: (prev.custom_blocks || []).filter(b => b.id !== blockId) } : prev);
                } else {
                  apiClient.getWorkflowCatalog().then(setCatalog).catch(() => {});
                }
              }}
              onEditCustomBlock={(block) => {
                setEditingCustomBlock(block);
                setShowCreateCustomBlock(true);
              }}
            />
          </div>
          </>
        )}
        <div className="flex-1 min-w-0 min-h-0 relative" key={`canvas-${showCodePanel}-${canvasResetKey}`}>
          {/* Mobile-only toggle for the block sidebar drawer. */}
          {catalog && (
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-background/95 border rounded-lg px-3 py-2 shadow-sm text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <Blocks className="w-3.5 h-3.5" /> Blocks
            </button>
          )}
          {IS_DEMO_MODE && bundleWorkflows.length > 1 && (
            <div className="absolute top-3 max-md:top-16 left-1/2 -translate-x-1/2 z-10 w-max max-w-[calc(100%-16px)]">
              <div className="flex items-center gap-1 flex-wrap justify-center bg-background/95 border rounded-lg px-2 py-1.5 shadow-sm">
                <span className="text-[11px] text-muted-foreground pr-1 whitespace-nowrap">Shared workflows:</span>
                {bundleWorkflows.map((bw, i) => (
                  <button
                    key={bw.id}
                    onClick={() => {
                      if (i === bundleIndex) return;
                      setBundleIndex(i);
                      applySharedSnapshot(bw);
                    }}
                    title={bw.description || bw.name}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-colors max-w-[140px] truncate ${
                      i === bundleIndex
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {bw.name || `Workflow ${i + 1}`}
                  </button>
                ))}
              </div>
            </div>
          )}
          <WorkflowCanvas
            definition={enrichedDefinition}
            onChange={setDefinition}
            onNodeSelect={setSelectedNodeId}
            selectedNodeId={selectedNodeId}
          />
        </div>
        {selectedNode && catalog && (
          <>
          <div
            onMouseDown={startPanelResize}
            onMouseEnter={() => {
              if (resizePanelLineRef.current) {
                resizePanelLineRef.current.style.transitionDelay = '400ms';
                resizePanelLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.3)';
              }
            }}
            onMouseLeave={() => {
              if (resizePanelLineRef.current && !isResizingPanel.current) {
                resizePanelLineRef.current.style.transitionDelay = '0ms';
                resizePanelLineRef.current.style.backgroundColor = 'transparent';
              }
            }}
            className="shrink-0 cursor-col-resize z-20 flex items-stretch justify-center max-md:hidden"
            style={{ width: '9px', marginLeft: '-4px', marginRight: '-5px' }}
          >
            <div
              ref={resizePanelLineRef}
              className="w-[3px] rounded-full bg-transparent transition-colors"
              style={{ transitionDuration: '150ms', transitionDelay: '0ms', pointerEvents: 'none' }}
            />
          </div>
          <div
            className="shrink-0 flex flex-col h-full max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:z-40 max-md:h-auto max-md:bg-background max-md:border-l max-md:shadow-xl max-md:!w-[min(68vw,18rem)]"
            style={{ width: configPanelWidth }}
          >
            <NodeConfigPanel
              node={selectedNode}
              actionTemplates={catalog.actions}
              onUpdate={handleNodeUpdate}
              onClose={() => setSelectedNodeId(null)}
              githubRepos={githubRepos}
              gitlabRepos={gitlabRepos}
              confluenceSpaces={confluenceSpaces}
              jiraProjects={jiraProjects}
              linearTeams={linearTeams}
              clusters={clusters}
              awsAccounts={awsAccounts}
              gcpProjects={gcpProjects}
              vercelProjects={vercelProjects}
              railwayProjects={railwayProjects}
              flyioApps={flyioApps}
              nebiusProjects={nebiusProjects}
              nebiusClusters={nebiusClusters}
              beamDeployments={beamDeployments}
              beamContainers={beamContainers}
              beamMachines={beamMachines}
              beamSandboxes={beamSandboxes}
              daytonaSandboxes={daytonaSandboxes}
              daytonaSnapshots={daytonaSnapshots}
              daytonaVolumes={daytonaVolumes}
              daytonaTargets={daytonaTargets}
              supabaseProjects={supabaseProjects}
              supabaseBranches={supabaseBranches}
              planetscaleDatabases={planetscaleDatabases}
              planetscaleBranches={planetscaleBranches}
              planetscaleDeployRequests={planetscaleDeployRequests}
              neonProjects={neonProjects}
              neonBranches={neonBranches}
              neonEndpoints={neonEndpoints}
              clickhouseServices={clickhouseServices}
              clickhouseClickPipes={clickhouseClickPipes}
              clickhouseBackups={clickhouseBackups}
              jenkinsJobs={jenkinsJobs}
              circleciProjects={circleciProjects}
              sonarcloudProjects={sonarcloudProjects}
              oktaUsers={oktaUsers}
              oktaGroups={oktaGroups}
              terraformWorkspaces={terraformWorkspaces}
              databricksJobs={databricksJobs}
              databricksClusters={databricksClusters}
              databricksPipelines={databricksPipelines}
              databricksWarehouses={databricksWarehouses}
              pulumiStacks={pulumiStacks}
              vaultMounts={vaultMounts}
              vaultPolicies={vaultPolicies}
              vaultDatabaseMounts={vaultDatabaseMounts}
              infisicalProjects={infisicalProjects}
              infisicalSecretSyncs={infisicalSecretSyncs}
              railwayServices={railwayServices}
              railwayEnvironments={railwayEnvironments}
              cloudflareZones={catalog.cloudflare_zones || []}
              cloudflareWorkers={catalog.cloudflare_workers || []}
              cloudflareLBPools={catalog.cloudflare_lb_pools || []}
              cloudflareTunnels={catalog.cloudflare_tunnels || []}
              cloudflareAccessApps={catalog.cloudflare_access_apps || []}
              allNodes={definition.nodes}
              edges={definition.edges}
              triggerVariables={catalog.trigger_variables}
              customBlocks={catalog.custom_blocks}
            />
          </div>
          </>
        )}

        <div style={{ display: showCodePanel ? 'contents' : 'none' }}>
          <div
            onMouseDown={startCodePanelResize}
            onMouseEnter={() => {
              if (resizeCodePanelLineRef.current) {
                resizeCodePanelLineRef.current.style.transitionDelay = '400ms';
                resizeCodePanelLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.3)';
              }
            }}
            onMouseLeave={() => {
              if (resizeCodePanelLineRef.current && !isResizingCodePanel.current) {
                resizeCodePanelLineRef.current.style.transitionDelay = '0ms';
                resizeCodePanelLineRef.current.style.backgroundColor = 'transparent';
              }
            }}
            className="shrink-0 cursor-col-resize z-20 flex items-stretch justify-center max-md:hidden"
            style={{ width: '9px', marginLeft: '-4px', marginRight: '-5px' }}
          >
            <div
              ref={resizeCodePanelLineRef}
              className="w-[3px] rounded-full bg-transparent transition-colors"
              style={{ transitionDuration: '150ms', transitionDelay: '0ms', pointerEvents: 'none' }}
            />
          </div>
          {/* Mobile: the code panel becomes a right-anchored drawer, mirroring
              NodeConfigPanel. In-flow it squeezed the canvas to a sliver and,
              with z-index auto, the Blocks button (z-10) and the Workflow Agent
              pill (z-20) painted on top of it. The `!` on the width is needed to
              beat the inline desktop width below. */}
          <div
            className="shrink-0 flex flex-col h-full max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:z-40 max-md:h-auto max-md:bg-background max-md:border-l max-md:shadow-xl max-md:!w-[min(68vw,18rem)]"
            style={{ width: codePanelWidth }}
          >
            <WorkflowCodePanel
              workflowName={name}
              description={description}
              definition={definition}
              triggerConfig={(() => {
                const tn = enrichedDefinition.nodes.find(n => n.type === 'trigger');
                if (!tn) return triggerConfig;
                return {
                  source: (tn.data.source || 'kubernetes') as string,
                  signals: [{ signal_type: tn.data.signal_type || 'any', filters: tn.data.signal_filter || {} }],
                  cooldown_hours: triggerConfig.cooldown_hours || 0,
                  cooldown_minutes: triggerConfig.cooldown_minutes || 0,
                  no_cooldown: triggerConfig.no_cooldown || false,
                  max_concurrent_executions: triggerConfig.max_concurrent_executions || 3,
                };
              })()}
              onClose={() => setShowCodePanel(false)}
              regenerateTrigger={codePanelRegenerateTrigger}
            />
          </div>
        </div>
      </div>

      {/* Floating NL chat — pinned to bottom center of the entire page. The
          md:ml-32 (128px) offset compensates for the platform sidebar, which
          is absent in demo mode and collapsed behind the hamburger on mobile.
          Mobile drops below the Blocks drawer (z-40) and its backdrop (z-30). */}
      {IS_DEMO_MODE ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-md:z-20">
          <WorkflowNLInput
            // In a demo bundle, remount on tab switch so the agent input
            // re-seeds from the selected workflow's stored prompt (the input
            // keeps its own draft state and ignores prop changes once filled).
            key={bundleWorkflows.length > 1 ? `nl-bundle-${bundleIndex}` : 'nl'}
            onGenerated={handleNLGenerated}
            initialPrompt={nlPrompt}
            simulated={simulated}
          />
        </div>
      ) : usesLegacyNLInput ? (
        // Workflows built before the conversational agent shipped keep the
        // one-shot Workflow Agent they were created with, so they look and
        // behave exactly as they used to.
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-md:z-20 md:ml-32">
          <WorkflowNLInput
            onGenerated={handleNLGenerated}
            initialPrompt={nlPrompt}
            simulated={simulated}
          />
        </div>
      ) : (
        <>
          {/* Conversational Workflow Agent: the pill opens a chat panel that
              builds and edits the workflow directly on the canvas. */}
          {!builderChatOpen && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-md:z-20 md:ml-32">
              <button
                onClick={() => setBuilderChatOpen(true)}
                className="nl-prompt-btn relative inline-flex items-center gap-2 px-5 h-10 max-md:h-auto max-md:min-h-10 max-md:py-2.5 text-sm font-medium shadow-lg"
              >
                <span className="nl-prompt-btn-border" />
                <span className="nl-prompt-btn-bg" />
                <span className="relative z-10 flex items-center gap-2 text-primary-foreground">
                  <Sparkles className="w-4 h-4" />
                  Describe workflow in natural language...
                </span>
              </button>
            </div>
          )}
          {/* Kept mounted (CSS-hidden) so the conversation survives closing. */}
          <div
            className={
              builderChatOpen
                ? 'fixed inset-y-0 right-0 z-50 max-md:!w-full border-l border-border bg-background shadow-2xl flex flex-col'
                : 'hidden'
            }
            style={builderChatOpen ? { width: chatPanelWidth } : undefined}
          >
            {/* Drag handle: resize the panel to trade chat space for canvas
                space. Same look as the Library page's assistant handle. */}
            <div
              onMouseDown={(e) => { e.preventDefault(); startChatResize(); }}
              onMouseEnter={() => {
                if (!isResizingChat.current && resizeChatLineRef.current) {
                  resizeChatLineRef.current.style.transitionDelay = '300ms';
                  resizeChatLineRef.current.style.backgroundColor = 'hsl(var(--border))';
                }
              }}
              onMouseLeave={() => {
                if (!isResizingChat.current && resizeChatLineRef.current) {
                  resizeChatLineRef.current.style.transitionDelay = '0ms';
                  resizeChatLineRef.current.style.backgroundColor = 'transparent';
                }
              }}
              className="absolute inset-y-0 cursor-col-resize z-20 flex items-stretch justify-center max-md:hidden"
              style={{ width: '9px', left: '-4px' }}
            >
              <div
                ref={resizeChatLineRef}
                className="w-[3px] rounded-full bg-transparent transition-colors"
                style={{ transitionDuration: '150ms', transitionDelay: '0ms', pointerEvents: 'none' }}
              />
            </div>
            <WorkflowSuggestionsChat
              builderMode={{
                onWorkflowGenerated: handleChatWorkflowGenerated,
                getCurrentWorkflow: () => currentWorkflowRef.current,
                simulated,
                contextId: builderChatContextId,
              }}
              onClose={() => setBuilderChatOpen(false)}
            />
          </div>
        </>
      )}

      <SimulatedModePromptModal
        open={showSimulatedPrompt}
        onEnable={() => {
          setSimulated(true);
          setShowSimulatedPrompt(false);
        }}
        onDismiss={() => setShowSimulatedPrompt(false)}
      />

      <CreateCustomBlockDialog
        open={showCreateCustomBlock}
        onOpenChange={(v) => {
          setShowCreateCustomBlock(v);
          if (!v) setEditingCustomBlock(null);
        }}
        onCreated={() => {
          // Demo blocks are merged locally via onCreatedLocal; refetching the
          // (static) public catalog would wipe them.
          if (!IS_DEMO_MODE) {
            apiClient.getWorkflowCatalog().then(setCatalog).catch(() => {});
          }
        }}
        onCreatedLocal={(block) => {
          setCatalog(prev => {
            if (!prev) return prev;
            const blocks = prev.custom_blocks || [];
            const idx = blocks.findIndex(b => b.id === block.id);
            const next = idx >= 0 ? blocks.map(b => (b.id === block.id ? block : b)) : [...blocks, block];
            return { ...prev, custom_blocks: next };
          });
        }}
        existingBlocks={catalog?.custom_blocks || []}
        editingBlock={editingCustomBlock}
      />

      {IS_DEMO_MODE && (
        <>
          <DemoSignupModal
            open={demoSignupCTA !== null}
            onClose={() => setDemoSignupCTA(null)}
            title={demoSignupCTA === 'test' ? 'Sign up to test this workflow' : 'Sign up to save this workflow'}
            description={
              demoSignupCTA === 'test'
                ? 'This is the demo builder — workflows built here can be generated and edited, but not test-run. Start the 14-day free trial with up to $1,000 in credits (no credit card required) to test this workflow with mocked outputs and run it for real against your integrations.'
                : 'This is the demo builder — workflows built here can be generated and edited, but not saved. Start the 14-day free trial with up to $1,000 in credits (no credit card required) to save this workflow, connect your integrations, and run it for real.'
            }
          />
          <DemoShareModal
            url={demoShareUrl}
            onClose={() => setDemoShareUrl('')}
            bundleCount={sessionShareIds.length}
            bundleUrl={demoBundleUrl}
            bundleCreating={demoBundleCreating}
            onCreateBundle={handleDemoBundle}
          />
        </>
      )}
    </div>
  );
};

export default WorkflowBuilder;
